import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Warehouse, Package, AlertTriangle, PackageX, ArrowRightLeft, Plus, Edit, Trash2,
  Search, ChevronLeft, ChevronRight, ArrowUpCircle, ArrowDownCircle, RefreshCw, History
} from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => (n || 0).toLocaleString('id-ID');

const typeLabels = {
  in: 'Stok Masuk', out: 'Stok Keluar', adjustment: 'Koreksi',
  transfer_in: 'Transfer Masuk', transfer_out: 'Transfer Keluar',
  sale: 'Penjualan', return: 'Retur',
};
const typeColors = {
  in: 'bg-green-100 text-green-700', out: 'bg-red-100 text-red-700',
  adjustment: 'bg-blue-100 text-blue-700',
  transfer_in: 'bg-purple-100 text-purple-700', transfer_out: 'bg-orange-100 text-orange-700',
  sale: 'bg-rose-100 text-rose-700', return: 'bg-teal-100 text-teal-700',
};

export default function Inventory() {
  const [tab, setTab] = useState('summary');
  const [warehouses, setWarehouses] = useState([]);

  // Summary state
  const [summary, setSummary] = useState(null);

  // Products state
  const [products, setProducts] = useState([]);
  const [prodTotal, setProdTotal] = useState(0);
  const [prodSearch, setProdSearch] = useState('');
  const [prodStatus, setProdStatus] = useState('');
  const [prodPage, setProdPage] = useState(1);
  const LIMIT = 30;

  // Movements state
  const [movements, setMovements] = useState([]);
  const [movTotal, setMovTotal] = useState(0);
  const [movProductId, setMovProductId] = useState('');
  const [movWhId, setMovWhId] = useState('');
  const [movType, setMovType] = useState('');
  const [movPage, setMovPage] = useState(1);

  // Dialogs
  const [whDialog, setWhDialog] = useState(false);
  const [adjustDialog, setAdjustDialog] = useState(false);
  const [bulkDialog, setBulkDialog] = useState(false);
  const [transferDialog, setTransferDialog] = useState(false);

  // Forms
  const [whForm, setWhForm] = useState({ name: '', address: '', is_default: false });
  const [editWhId, setEditWhId] = useState(null);

  const [adjustForm, setAdjustForm] = useState({ product_id: '', warehouse_id: '', quantity: 0, type: 'in', notes: '' });
  const [adjustProductName, setAdjustProductName] = useState('');

  const [transferForm, setTransferForm] = useState({ product_id: '', source_warehouse_id: '', destination_warehouse_id: '', quantity: 0, notes: '' });

  const [bulkWh, setBulkWh] = useState('');
  const [bulkType, setBulkType] = useState('in');
  const [bulkNotes, setBulkNotes] = useState('');
  const [bulkItems, setBulkItems] = useState([]);

  const [loading, setLoading] = useState(false);

  // ──────────── Fetch ────────────

  const fetchWarehouses = useCallback(async () => {
    try {
      const { data } = await api.get('/tenant/warehouses');
      setWarehouses(data.warehouses || []);
    } catch { }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const { data } = await api.get('/tenant/inventory/summary');
      setSummary(data);
    } catch { }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const { data } = await api.get('/tenant/inventory/products', {
        params: { search: prodSearch, stock_status: prodStatus, skip: (prodPage - 1) * LIMIT, limit: LIMIT }
      });
      setProducts(data.products || []);
      setProdTotal(data.total || 0);
    } catch { }
  }, [prodSearch, prodStatus, prodPage]);

  const fetchMovements = useCallback(async () => {
    try {
      const { data } = await api.get('/tenant/inventory/movements', {
        params: { product_id: movProductId, warehouse_id: movWhId, type: movType, skip: (movPage - 1) * LIMIT, limit: LIMIT }
      });
      setMovements(data.movements || []);
      setMovTotal(data.total || 0);
    } catch { }
  }, [movProductId, movWhId, movType, movPage]);

  useEffect(() => { fetchWarehouses(); }, [fetchWarehouses]);
  useEffect(() => { if (tab === 'summary') fetchSummary(); }, [tab, fetchSummary]);
  useEffect(() => { if (tab === 'products') fetchProducts(); }, [tab, fetchProducts]);
  useEffect(() => { if (tab === 'movements') fetchMovements(); }, [tab, fetchMovements]);

  // ──────────── Warehouse CRUD ────────────

  const saveWarehouse = async () => {
    try {
      if (editWhId) {
        await api.put(`/tenant/warehouses/${editWhId}`, whForm);
        toast.success('Gudang berhasil diupdate');
      } else {
        await api.post('/tenant/warehouses', whForm);
        toast.success('Gudang berhasil ditambahkan');
      }
      setWhForm({ name: '', address: '', is_default: false });
      setEditWhId(null);
      fetchWarehouses();
      fetchSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan gudang');
    }
  };

  const deleteWarehouse = async (id) => {
    if (!window.confirm('Hapus gudang ini?')) return;
    try {
      await api.delete(`/tenant/warehouses/${id}`);
      toast.success('Gudang berhasil dihapus');
      fetchWarehouses();
      fetchSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menghapus gudang');
    }
  };

  // ──────────── Stock Adjust ────────────

  const openAdjust = (product = null) => {
    setAdjustForm({
      product_id: product?.id || '',
      warehouse_id: warehouses[0]?.id || '',
      quantity: 0,
      type: 'in',
      notes: '',
    });
    setAdjustProductName(product?.name || '');
    setAdjustDialog(true);
  };

  const submitAdjust = async () => {
    if (!adjustForm.product_id || !adjustForm.warehouse_id || adjustForm.quantity <= 0) {
      toast.error('Lengkapi semua field dan pastikan jumlah > 0');
      return;
    }
    setLoading(true);
    try {
      await api.post('/tenant/inventory/adjust', adjustForm);
      toast.success('Stok berhasil diupdate');
      setAdjustDialog(false);
      fetchProducts();
      fetchSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal adjust stok');
    }
    setLoading(false);
  };

  // ──────────── Stock Transfer ────────────

  const openTransfer = (product = null) => {
    setTransferForm({
      product_id: product?.id || '',
      source_warehouse_id: warehouses[0]?.id || '',
      destination_warehouse_id: warehouses[1]?.id || '',
      quantity: 0,
      notes: '',
    });
    setTransferDialog(true);
  };

  const submitTransfer = async () => {
    if (!transferForm.product_id || !transferForm.source_warehouse_id || !transferForm.destination_warehouse_id || transferForm.quantity <= 0) {
      toast.error('Lengkapi semua field');
      return;
    }
    setLoading(true);
    try {
      await api.post('/tenant/inventory/transfer', transferForm);
      toast.success('Transfer berhasil');
      setTransferDialog(false);
      fetchProducts();
      fetchSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal transfer stok');
    }
    setLoading(false);
  };

  // ──────────── Bulk Adjust ────────────

  const openBulk = () => {
    setBulkWh(warehouses[0]?.id || '');
    setBulkType('in');
    setBulkNotes('');
    setBulkItems(products.map(p => ({ product_id: p.id, name: p.name, stock: p.stock, qty: 0 })));
    setBulkDialog(true);
  };

  const submitBulk = async () => {
    const adjustments = bulkItems
      .filter(i => i.qty > 0)
      .map(i => ({ product_id: i.product_id, warehouse_id: bulkWh, quantity: i.qty, type: bulkType, notes: bulkNotes }));
    if (!adjustments.length) { toast.error('Tidak ada item dengan qty > 0'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/tenant/inventory/bulk-adjust', { adjustments });
      toast.success(data.message);
      setBulkDialog(false);
      fetchProducts();
      fetchSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal bulk adjust');
    }
    setLoading(false);
  };

  // ──────────── Render ────────────

  const whMap = {};
  warehouses.forEach(w => { whMap[w.id] = w.name; });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Warehouse size={24} /> Inventori</h1>
          <p className="text-gray-500 text-sm">Kelola stok produk & gudang</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setEditWhId(null); setWhForm({ name: '', address: '', is_default: false }); setWhDialog(true); }}>
            <Warehouse size={16} className="mr-1" /> Kelola Gudang
          </Button>
          <Button onClick={() => openAdjust()}><Plus size={16} className="mr-1" /> Adjust Stok</Button>
          {warehouses.length >= 2 && (
            <Button variant="outline" onClick={() => openTransfer()}><ArrowRightLeft size={16} className="mr-1" /> Transfer</Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="summary">Ringkasan</TabsTrigger>
          <TabsTrigger value="products">Stok Produk</TabsTrigger>
          <TabsTrigger value="movements">Riwayat Stok</TabsTrigger>
        </TabsList>

        {/* ─── Summary ─── */}
        <TabsContent value="summary" className="space-y-6">
          {summary && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-blue-100"><Package size={20} className="text-blue-600" /></div>
                      <div>
                        <p className="text-sm text-gray-500">Produk Fisik</p>
                        <p className="text-2xl font-bold">{summary.total_physical}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-green-100"><Warehouse size={20} className="text-green-600" /></div>
                      <div>
                        <p className="text-sm text-gray-500">Total Stok</p>
                        <p className="text-2xl font-bold">{fmt(summary.total_stock)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-yellow-100"><AlertTriangle size={20} className="text-yellow-600" /></div>
                      <div>
                        <p className="text-sm text-gray-500">Stok Rendah</p>
                        <p className="text-2xl font-bold text-yellow-600">{summary.low_stock_count}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-red-100"><PackageX size={20} className="text-red-600" /></div>
                      <div>
                        <p className="text-sm text-gray-500">Stok Habis</p>
                        <p className="text-2xl font-bold text-red-600">{summary.out_of_stock_count}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Warehouse Summary */}
              {summary.warehouse_summary?.length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="text-lg">Stok per Gudang</CardTitle></CardHeader>
                  <CardContent>
                    <table className="w-full text-sm">
                      <thead><tr className="border-b">
                        <th className="text-left py-2">Gudang</th>
                        <th className="text-right py-2">Jumlah Produk</th>
                        <th className="text-right py-2">Total Stok</th>
                      </tr></thead>
                      <tbody>
                        {summary.warehouse_summary.map((ws, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="py-2 font-medium">{ws.warehouse_name}</td>
                            <td className="py-2 text-right">{ws.product_count}</td>
                            <td className="py-2 text-right font-semibold">{fmt(ws.total_stock)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              )}

              {/* Low Stock Alerts */}
              {summary.low_stock_products?.length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="text-lg flex items-center gap-2"><AlertTriangle size={18} className="text-yellow-500" /> Peringatan Stok Rendah</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {summary.low_stock_products.map(p => (
                        <div key={p.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            {p.images?.[0] ? (
                              <img src={p.images[0]} alt="" className="w-10 h-10 rounded object-cover" />
                            ) : (
                              <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center"><Package size={16} className="text-gray-400" /></div>
                            )}
                            <div>
                              <p className="font-medium">{p.name}</p>
                              {p.sku && <p className="text-xs text-gray-500">SKU: {p.sku}</p>}
                            </div>
                          </div>
                          <Badge variant="outline" className="bg-yellow-100 text-yellow-700">{p.stock} unit</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* ─── Products ─── */}
        <TabsContent value="products" className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input placeholder="Cari produk..." className="pl-9" value={prodSearch} onChange={e => { setProdSearch(e.target.value); setProdPage(1); }} />
                </div>
                <Select value={prodStatus} onValueChange={v => { setProdStatus(v === 'all' ? '' : v); setProdPage(1); }}>
                  <SelectTrigger className="w-40"><SelectValue placeholder="Status Stok" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua</SelectItem>
                    <SelectItem value="in">Stok Tersedia</SelectItem>
                    <SelectItem value="low">Stok Rendah</SelectItem>
                    <SelectItem value="out">Stok Habis</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={openBulk}><RefreshCw size={14} className="mr-1" /> Bulk Adjust</Button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b bg-gray-50">
                    <th className="text-left py-2 px-3">Produk</th>
                    <th className="text-left py-2 px-3">SKU</th>
                    {warehouses.map(w => <th key={w.id} className="text-right py-2 px-3">{w.name}</th>)}
                    <th className="text-right py-2 px-3">Total</th>
                    <th className="text-center py-2 px-3">Status</th>
                    <th className="text-right py-2 px-3">Aksi</th>
                  </tr></thead>
                  <tbody>
                    {products.map(p => {
                      const wsMap = {};
                      (p.warehouse_stocks || []).forEach(ws => { wsMap[ws.warehouse_id] = ws.stock; });
                      const status = p.stock <= 0 ? 'out' : p.stock <= 5 ? 'low' : 'in';
                      return (
                        <tr key={p.id} className="border-b hover:bg-gray-50">
                          <td className="py-2 px-3">
                            <div className="flex items-center gap-2">
                              {p.images?.[0] ? (
                                <img src={p.images[0]} alt="" className="w-8 h-8 rounded object-cover" />
                              ) : (
                                <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center"><Package size={14} className="text-gray-400" /></div>
                              )}
                              <span className="font-medium">{p.name}</span>
                            </div>
                          </td>
                          <td className="py-2 px-3 text-gray-500">{p.sku || '-'}</td>
                          {warehouses.map(w => (
                            <td key={w.id} className="py-2 px-3 text-right">{wsMap[w.id] ?? 0}</td>
                          ))}
                          <td className="py-2 px-3 text-right font-semibold">{p.stock}</td>
                          <td className="py-2 px-3 text-center">
                            {status === 'out' && <Badge className="bg-red-100 text-red-700">Habis</Badge>}
                            {status === 'low' && <Badge className="bg-yellow-100 text-yellow-700">Rendah</Badge>}
                            {status === 'in' && <Badge className="bg-green-100 text-green-700">Tersedia</Badge>}
                          </td>
                          <td className="py-2 px-3 text-right">
                            <div className="flex gap-1 justify-end">
                              <Button size="sm" variant="ghost" onClick={() => openAdjust(p)} title="Adjust stok">
                                <ArrowUpCircle size={14} />
                              </Button>
                              {warehouses.length >= 2 && (
                                <Button size="sm" variant="ghost" onClick={() => { setTransferForm({ ...transferForm, product_id: p.id }); setTransferDialog(true); }} title="Transfer">
                                  <ArrowRightLeft size={14} />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {!products.length && <tr><td colSpan={warehouses.length + 5} className="py-8 text-center text-gray-400">Tidak ada produk fisik</td></tr>}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {prodTotal > LIMIT && (
                <div className="flex items-center justify-between mt-4">
                  <span className="text-sm text-gray-500">Total: {prodTotal} produk</span>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" disabled={prodPage <= 1} onClick={() => setProdPage(p => p - 1)}><ChevronLeft size={14} /></Button>
                    <span className="text-sm">Hal. {prodPage} / {Math.ceil(prodTotal / LIMIT)}</span>
                    <Button size="sm" variant="outline" disabled={prodPage >= Math.ceil(prodTotal / LIMIT)} onClick={() => setProdPage(p => p + 1)}><ChevronRight size={14} /></Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Movements ─── */}
        <TabsContent value="movements" className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <Select value={movWhId || 'all'} onValueChange={v => { setMovWhId(v === 'all' ? '' : v); setMovPage(1); }}>
                  <SelectTrigger className="w-44"><SelectValue placeholder="Semua Gudang" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Gudang</SelectItem>
                    {warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={movType || 'all'} onValueChange={v => { setMovType(v === 'all' ? '' : v); setMovPage(1); }}>
                  <SelectTrigger className="w-44"><SelectValue placeholder="Semua Tipe" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Tipe</SelectItem>
                    {Object.entries(typeLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b bg-gray-50">
                    <th className="text-left py-2 px-3">Waktu</th>
                    <th className="text-left py-2 px-3">Produk</th>
                    <th className="text-left py-2 px-3">Gudang</th>
                    <th className="text-center py-2 px-3">Tipe</th>
                    <th className="text-right py-2 px-3">Jumlah</th>
                    <th className="text-right py-2 px-3">Stok</th>
                    <th className="text-left py-2 px-3">Catatan</th>
                    <th className="text-left py-2 px-3">User</th>
                  </tr></thead>
                  <tbody>
                    {movements.map(m => (
                      <tr key={m.id} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-3 text-gray-500 whitespace-nowrap">{new Date(m.created_at).toLocaleString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                        <td className="py-2 px-3 font-medium">{m.product_name}</td>
                        <td className="py-2 px-3">{m.warehouse_name}</td>
                        <td className="py-2 px-3 text-center"><Badge className={typeColors[m.type] || 'bg-gray-100'}>{typeLabels[m.type] || m.type}</Badge></td>
                        <td className={`py-2 px-3 text-right font-semibold ${m.quantity >= 0 ? 'text-green-600' : 'text-red-600'}`}>{m.quantity >= 0 ? '+' : ''}{m.quantity}</td>
                        <td className="py-2 px-3 text-right text-gray-500">{m.previous_stock} → {m.new_stock}</td>
                        <td className="py-2 px-3 text-gray-500 max-w-[200px] truncate">{m.notes || '-'}</td>
                        <td className="py-2 px-3 text-gray-500">{m.created_by_email || m.created_by}</td>
                      </tr>
                    ))}
                    {!movements.length && <tr><td colSpan={8} className="py-8 text-center text-gray-400">Belum ada riwayat stok</td></tr>}
                  </tbody>
                </table>
              </div>

              {movTotal > LIMIT && (
                <div className="flex items-center justify-between mt-4">
                  <span className="text-sm text-gray-500">Total: {movTotal} record</span>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" disabled={movPage <= 1} onClick={() => setMovPage(p => p - 1)}><ChevronLeft size={14} /></Button>
                    <span className="text-sm">Hal. {movPage} / {Math.ceil(movTotal / LIMIT)}</span>
                    <Button size="sm" variant="outline" disabled={movPage >= Math.ceil(movTotal / LIMIT)} onClick={() => setMovPage(p => p + 1)}><ChevronRight size={14} /></Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ─── Warehouse Management Dialog ─── */}
      <Dialog open={whDialog} onOpenChange={setWhDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Kelola Gudang</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Existing warehouses */}
            {warehouses.length > 0 && (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {warehouses.map(w => (
                  <div key={w.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{w.name} {w.is_default && <Badge variant="outline" className="ml-1 text-xs">Default</Badge>}</p>
                      {w.address && <p className="text-xs text-gray-500">{w.address}</p>}
                      <p className="text-xs text-gray-400">{w.total_stock ?? 0} stok • {w.product_count ?? 0} produk</p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => { setEditWhId(w.id); setWhForm({ name: w.name, address: w.address || '', is_default: w.is_default }); }}>
                        <Edit size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteWarehouse(w.id)}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <hr />
            <p className="font-medium text-sm">{editWhId ? 'Edit Gudang' : 'Tambah Gudang Baru'}</p>
            <div className="grid gap-3">
              <div>
                <Label>Nama Gudang *</Label>
                <Input value={whForm.name} onChange={e => setWhForm({ ...whForm, name: e.target.value })} placeholder="Gudang Jakarta" />
              </div>
              <div>
                <Label>Alamat</Label>
                <Input value={whForm.address} onChange={e => setWhForm({ ...whForm, address: e.target.value })} placeholder="Jl. Contoh No. 1" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={whForm.is_default} onChange={e => setWhForm({ ...whForm, is_default: e.target.checked })} />
                <Label>Jadikan gudang default</Label>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              {editWhId && <Button variant="outline" onClick={() => { setEditWhId(null); setWhForm({ name: '', address: '', is_default: false }); }}>Batal Edit</Button>}
              <Button onClick={saveWarehouse} disabled={!whForm.name}>{editWhId ? 'Update' : 'Tambah'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Adjust Stock Dialog ─── */}
      <Dialog open={adjustDialog} onOpenChange={setAdjustDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Adjust Stok {adjustProductName && `- ${adjustProductName}`}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {!adjustForm.product_id && (
              <div>
                <Label>Pilih Produk *</Label>
                <Select value={adjustForm.product_id} onValueChange={v => setAdjustForm({ ...adjustForm, product_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Pilih produk..." /></SelectTrigger>
                  <SelectContent>
                    {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.stock})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Gudang *</Label>
              <Select value={adjustForm.warehouse_id} onValueChange={v => setAdjustForm({ ...adjustForm, warehouse_id: v })}>
                <SelectTrigger><SelectValue placeholder="Pilih gudang..." /></SelectTrigger>
                <SelectContent>
                  {warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipe *</Label>
              <Select value={adjustForm.type} onValueChange={v => setAdjustForm({ ...adjustForm, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in"><span className="flex items-center gap-1"><ArrowUpCircle size={14} className="text-green-600" /> Stok Masuk</span></SelectItem>
                  <SelectItem value="out"><span className="flex items-center gap-1"><ArrowDownCircle size={14} className="text-red-600" /> Stok Keluar</span></SelectItem>
                  <SelectItem value="adjustment"><span className="flex items-center gap-1"><RefreshCw size={14} className="text-blue-600" /> Koreksi (set ke nilai)</span></SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{adjustForm.type === 'adjustment' ? 'Stok Baru' : 'Jumlah'} *</Label>
              <Input type="number" min={0} value={adjustForm.quantity} onChange={e => setAdjustForm({ ...adjustForm, quantity: parseInt(e.target.value) || 0 })} />
            </div>
            <div>
              <Label>Catatan</Label>
              <Textarea value={adjustForm.notes} onChange={e => setAdjustForm({ ...adjustForm, notes: e.target.value })} placeholder="Opsional..." rows={2} />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setAdjustDialog(false)}>Batal</Button>
              <Button onClick={submitAdjust} disabled={loading}>{loading ? 'Menyimpan...' : 'Simpan'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Transfer Dialog ─── */}
      <Dialog open={transferDialog} onOpenChange={setTransferDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Transfer Stok Antar Gudang</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Produk *</Label>
              <Select value={transferForm.product_id} onValueChange={v => setTransferForm({ ...transferForm, product_id: v })}>
                <SelectTrigger><SelectValue placeholder="Pilih produk..." /></SelectTrigger>
                <SelectContent>
                  {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.stock})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Gudang Asal *</Label>
              <Select value={transferForm.source_warehouse_id} onValueChange={v => setTransferForm({ ...transferForm, source_warehouse_id: v })}>
                <SelectTrigger><SelectValue placeholder="Dari gudang..." /></SelectTrigger>
                <SelectContent>
                  {warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Gudang Tujuan *</Label>
              <Select value={transferForm.destination_warehouse_id} onValueChange={v => setTransferForm({ ...transferForm, destination_warehouse_id: v })}>
                <SelectTrigger><SelectValue placeholder="Ke gudang..." /></SelectTrigger>
                <SelectContent>
                  {warehouses.filter(w => w.id !== transferForm.source_warehouse_id).map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Jumlah *</Label>
              <Input type="number" min={1} value={transferForm.quantity} onChange={e => setTransferForm({ ...transferForm, quantity: parseInt(e.target.value) || 0 })} />
            </div>
            <div>
              <Label>Catatan</Label>
              <Textarea value={transferForm.notes} onChange={e => setTransferForm({ ...transferForm, notes: e.target.value })} placeholder="Opsional..." rows={2} />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setTransferDialog(false)}>Batal</Button>
              <Button onClick={submitTransfer} disabled={loading}>{loading ? 'Memproses...' : 'Transfer'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Bulk Adjust Dialog ─── */}
      <Dialog open={bulkDialog} onOpenChange={setBulkDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Bulk Adjust Stok</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Gudang *</Label>
                <Select value={bulkWh} onValueChange={setBulkWh}>
                  <SelectTrigger><SelectValue placeholder="Pilih gudang..." /></SelectTrigger>
                  <SelectContent>
                    {warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tipe *</Label>
                <Select value={bulkType} onValueChange={setBulkType}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in">Stok Masuk</SelectItem>
                    <SelectItem value="out">Stok Keluar</SelectItem>
                    <SelectItem value="adjustment">Koreksi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Catatan</Label>
              <Input value={bulkNotes} onChange={e => setBulkNotes(e.target.value)} placeholder="Catatan untuk semua item..." />
            </div>

            <table className="w-full text-sm">
              <thead><tr className="border-b">
                <th className="text-left py-2">Produk</th>
                <th className="text-right py-2">Stok Saat Ini</th>
                <th className="text-right py-2 w-28">Jumlah</th>
              </tr></thead>
              <tbody>
                {bulkItems.map((item, i) => (
                  <tr key={item.product_id} className="border-b">
                    <td className="py-2">{item.name}</td>
                    <td className="py-2 text-right text-gray-500">{item.stock}</td>
                    <td className="py-2 text-right">
                      <Input
                        type="number" min={0} className="w-24 ml-auto text-right"
                        value={item.qty}
                        onChange={e => {
                          const newItems = [...bulkItems];
                          newItems[i] = { ...item, qty: parseInt(e.target.value) || 0 };
                          setBulkItems(newItems);
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setBulkDialog(false)}>Batal</Button>
              <Button onClick={submitBulk} disabled={loading}>{loading ? 'Memproses...' : `Simpan (${bulkItems.filter(i => i.qty > 0).length} item)`}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
